import type { ReactNode } from 'react'
import type { VoltageViolationMeter } from '../../types/amiAnalytics.ts'
import { Term } from '../common/Term.tsx'

interface Props {
  meters: VoltageViolationMeter[]
  windowDays: number
  totalViolationMeters: number
}

function Th({ children }: { children: ReactNode }) {
  return (
    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">
      {children}
    </th>
  )
}

export function VoltageViolationsTable({ meters, windowDays, totalViolationMeters }: Props) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">
        ANSI Band Violations — last {windowDays} days
        {totalViolationMeters > meters.length
          ? ` (showing ${meters.length} of ${totalViolationMeters})`
          : ` (${totalViolationMeters})`}
      </h3>
      <p className="text-[11px] text-gray-400 mb-2">
        Hours outside ANSI C84.1 Range A (0.95–1.05 pu); rates against
        eligible reporting hours. Sag/swell are transient event counts.
      </p>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr>
              <Th>Meter</Th>
              <Th>Phase</Th>
              <Th>Nominal</Th>
              <Th>Hrs low</Th>
              <Th>Hrs high</Th>
              <Th>Rate</Th>
              <Th><Term k="pu">Worst pu</Term></Th>
              <Th><Term k="sag-swell">Sag/Swell</Term></Th>
              <Th>Transformer</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {meters.map(m => (
              <tr key={`${m.meter_id}-${m.phase}`} className="hover:bg-gray-50">
                <td className="px-2 py-1.5">{m.meter_number ?? m.meter_id}</td>
                <td className="px-2 py-1.5">{m.phase}</td>
                <td className="px-2 py-1.5">{m.nominal_v ?? '—'} V</td>
                <td className="px-2 py-1.5 text-red-600 font-medium">
                  {m.hours_below || '—'}
                </td>
                <td className="px-2 py-1.5 text-amber-600 font-medium">
                  {m.hours_above || '—'}
                </td>
                <td className="px-2 py-1.5">{m.violation_rate_pct}%</td>
                <td className="px-2 py-1.5">
                  {m.worst_v_min_pu != null ? m.worst_v_min_pu.toFixed(3) : '—'}
                  {' / '}
                  {m.worst_v_max_pu != null ? m.worst_v_max_pu.toFixed(3) : '—'}
                </td>
                <td className="px-2 py-1.5">{m.sag_total}/{m.swell_total}</td>
                <td className="px-2 py-1.5 text-gray-500">
                  {m.transformer_name ?? m.transformer_id ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
